/**
 * CORE-1118 — why UI text on Web still rendered in the system stack.
 *
 * react-native-web gives every <Text>/<TextInput> a base class of its own
 * with `font: 14px System`, which its compiler expands to
 * `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, …`. Any call site
 * that names no family itself therefore paints system, not Nunito Sans — the
 * catalog's «Все направления», «80 практик», the tabs, «Загрузить ещё».
 *
 * The previous fix matched those classes from global.css by name
 * (`[class*="css-text-"]`, `:not([class*="r-fontFamily-"])`). That only works
 * in development: RN-Web emits `css-text-<hash>` / `r-fontFamily-<hash>` under
 * NODE_ENV !== 'production' and bare `css-<hash>` / `r-<hash>` in the
 * production bundle Vercel ships, so the selectors never matched on the stand.
 *
 * Nothing in CSS can sit strictly between RN-Web's base class and its atomic
 * `fontFamily` class (equal specificity, and RN-Web orders them inside ONE
 * sheet it inserts first in <head>). So instead of fighting the cascade we
 * rewrite the base rule in place, in RN-Web's own sheet: ordering stays
 * intact, explicit families (Lora display headings, uiFont()) keep winning,
 * and the class-name scheme is irrelevant. The rules exist by the time
 * App.tsx runs — RN-Web inserts them when the Text/TextInput modules evaluate.
 */

/** Literal stack, not --ag-font-ui: must hold before global.css loads. */
export const WEB_UI_FONT_STACK = "'Nunito Sans', 'Helvetica Neue', Arial, sans-serif";

/** RN-Web's StyleSheet element id (react-native-web/dist/exports/StyleSheet/dom). */
export const RN_WEB_STYLESHEET_ID = 'react-native-stylesheet';

const SYSTEM_STACK_MARKER = '-apple-system';

type StyleRuleLike = { style?: { fontFamily: string } };

/** Replaces the system stack in every matching rule; returns how many changed. */
export function rewriteSystemFontRules(rules: Iterable<StyleRuleLike>, stack: string = WEB_UI_FONT_STACK): number {
  let rewritten = 0;
  for (const rule of rules) {
    const style = rule.style;
    if (!style || typeof style.fontFamily !== 'string') continue;
    if (!style.fontFamily.includes(SYSTEM_STACK_MARKER)) continue;
    style.fontFamily = stack;
    rewritten += 1;
  }
  return rewritten;
}

/** Web-only entry point; safe no-op anywhere without a DOM or the RN-Web sheet. */
export function applyWebTextDefaultFont(doc: Pick<Document, 'getElementById'> | undefined = globalThis.document): number {
  // Test environments may expose a `document` stub without a real DOM API.
  if (typeof doc?.getElementById !== 'function') return 0;
  const element = doc.getElementById(RN_WEB_STYLESHEET_ID) as HTMLStyleElement | null;
  const sheet = element?.sheet;
  if (!sheet) return 0;
  try {
    return rewriteSystemFontRules(Array.from(sheet.cssRules) as unknown as StyleRuleLike[]);
  } catch {
    // A cross-origin or detached sheet throws on cssRules; leaving the default
    // in place is better than crashing bootstrap.
    return 0;
  }
}
